/** UI cascading alcohol display — keep in sync with backend/utils/alcoholDisplayFilter.js */

export const ALCOHOL_PERCENT_KEYS = ['60', '65', '68', '70', '72'];
export const V2_DISPLAY_PERCENT_KEYS = ['68', '70', '72'];

export const getAlcoholResults = (analysisResults) => {
  if (!analysisResults) return null;
  if (analysisResults.alcohol && analysisResults.alcohol.results) {
    return analysisResults.alcohol.results;
  }
  if (analysisResults.results) {
    return analysisResults.results;
  }
  return null;
};

export const getV2AlcoholResult = (analysisResults) => {
  const alcohol = analysisResults?.alcohol;
  if (!alcohol || typeof alcohol !== 'object') return null;
  if (alcohol.source === 'milkscreen-v2' || alcohol.decision) {
    return alcohol;
  }
  return null;
};

export const getV2AlcoholDecision = (analysisResults) => {
  const decision = getV2AlcoholResult(analysisResults)?.decision;
  if (!decision) return null;
  const normalized = String(decision).trim().toLowerCase();
  if (normalized === 'pass' || normalized === 'fail' || normalized === 'refer') {
    return normalized;
  }
  return normalized;
};

export const getV2FatValue = (analysisResults) => {
  const pred = analysisResults?.fat?.pred_f;
  if (Array.isArray(pred) && pred.length > 0 && pred[0] != null && pred[0] !== '') {
    const num = Number(pred[0]);
    return Number.isNaN(num) ? null : num;
  }
  return null;
};

export const isV2AlcoholReading = (reading) =>
  reading?.apiVersion === 'v2' || !!getV2AlcoholDecision(reading?.analysisResults);

export const formatV2AlcoholDecisionLabel = (decision) => {
  if (!decision) return null;
  return String(decision);
};

export const getAlcoholDecisionStatusClass = (decision) => {
  if (decision === 'pass') return 'status-pass';
  if (decision === 'fail') return 'status-fail';
  if (decision === 'refer') return 'status-refer';
  return 'status-info';
};

export const hasAlcoholUiResult = (analysisResults) =>
  !!(getV2AlcoholDecision(analysisResults) || getAlcoholResults(analysisResults));

export const normalizeAlcoholLabel = (label) => {
  if (!label) return null;
  const normalized = String(label).toLowerCase();
  if (normalized === 'fail' || normalized === 'f') return 'fail';
  if (normalized === 'pass' || normalized === 'p') return 'pass';
  return normalized;
};

/** First raw fail forces all higher alcohol % to display as fail (matches row badges). */
export const getAlcoholResultsForDisplay = (analysisResults) => {
  const results = getAlcoholResults(analysisResults);
  if (!results) return null;

  const displayResults = { ...results };
  let cascadeFail = false;

  ALCOHOL_PERCENT_KEYS.forEach((pct) => {
    const key = `alcohol_${pct}`;
    const item = results[key];
    if (!item || !item.label) return;

    const rawLabel = normalizeAlcoholLabel(item.label);
    let displayLabel;

    if (cascadeFail) {
      displayLabel = 'fail';
    } else if (rawLabel === 'fail') {
      cascadeFail = true;
      displayLabel = 'fail';
    } else {
      displayLabel = rawLabel || item.label;
    }

    displayResults[key] = { ...item, label: displayLabel };
  });

  return displayResults;
};

export const getDisplayLabelForPercentage = (analysisResults, percentage) => {
  const display = getAlcoholResultsForDisplay(analysisResults);
  if (!display) return null;
  const key = `alcohol_${percentage}`;
  const item = display[key];
  if (!item || !item.label) return null;
  return normalizeAlcoholLabel(item.label);
};

export const readingMatchesAlcoholUiFilter = (reading, percentage, filterValue) => {
  if (!filterValue || filterValue === 'all') return true;
  const pct = String(percentage);
  if (isV2AlcoholReading(reading) && V2_DISPLAY_PERCENT_KEYS.includes(pct)) {
    return getV2AlcoholDecision(reading.analysisResults) === filterValue;
  }
  const displayLabel = getDisplayLabelForPercentage(reading.analysisResults, percentage);
  if (!displayLabel) return false;
  return displayLabel === filterValue;
};

export const readingMatchesAlcoholDecisionFilter = (reading, filterValue) => {
  if (!filterValue || filterValue === 'all') return true;
  return getV2AlcoholDecision(reading.analysisResults) === filterValue;
};

export const readingMatchesAllAlcoholUiFilters = (reading, filters) => {
  const entries = [
    ['60', filters.alcohol60],
    ['65', filters.alcohol65],
    ['68', filters.alcohol68],
    ['70', filters.alcohol70],
    ['72', filters.alcohol72]
  ];
  for (const [pct, value] of entries) {
    if (value && value !== 'all' && !readingMatchesAlcoholUiFilter(reading, pct, value)) {
      return false;
    }
  }
  return true;
};
